// Central content + imagery. Swap these values when final copy arrives.

const u = (id, w = 1400) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;
const p = (url, w = 1400) => `${url}?auto=compress&cs=tinysrgb&w=${w}`;

export const IMAGES = {
  multifamily: [
    u("photo-1551361415-69c87624334f"),
    u("photo-1515263487990-61b07816b324"),
    u("photo-1612637968894-660373e23b03"),
    u("photo-1543071293-d91175a68672"),
  ],
  construction: [
    u("photo-1527335988388-b40ee248d80c"),
    u("photo-1602757115429-b4190ae087be"),
    p("https://images.pexels.com/photos/18078304/pexels-photo-18078304.jpeg"),
    p("https://images.pexels.com/photos/18283538/pexels-photo-18283538.jpeg"),
  ],
  team: [
    "/media/team/team0.jpg",
    "/media/team/team1.jpg",
    "/media/team/team2.jpg",
  ],
  facade: [
    u("photo-1611570885483-095b1b449aa3"),
    u("photo-1576831371356-d6e9411ae501"),
    u("photo-1453586857165-eb78d44460ca"),
    u("photo-1613490493576-7fde63acd811"),
  ],
  editorial: p("https://images.pexels.com/photos/27307400/pexels-photo-27307400.jpeg", 1800),
};

export const COMPANY = {
  name: "Next Horizon Builders",
  slogan: "Build Your Horizon with Next Horizon Builders.",
  subtitle:
    "Ground-up multifamily construction built with precision, integrity, safety, and long-term value across Southern California.",
  phone: "(310) 466-4666",
  phoneHref: "tel:+13104664666",
  email: "Info@nhgroups.com",
  website: "https://nhgroups.com",
  websiteLabel: "nhgroups.com",
  address: "Newport Coast, CA 92657",
  region: "Serving Southern California",
  license: "California CSLB License #1152358",
  hours: [
    { days: "Monday – Friday", time: "7:00 AM – 5:00 PM" },
  ],
  serviceAreas: ["Los Angeles", "Orange County", "Southern California"],
  founded: "2020",
};

export const HIGHLIGHTS = [
  "Licensed California General Contractor",
  "Specialized in Ground-Up Multifamily Construction",
  "Serving Southern California",
  "Safety-Focused Project Delivery",
  "Experienced Project Management & Field Coordination",
];

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Projects", to: "/projects" },
  { label: "Careers", to: "/careers" },
  { label: "Trade Partners", to: "/trade-partners" },
  { label: "Contact", to: "/contact" },
];

export const STATS = [
  { value: "Licensed", label: "CA CSLB #1152358" },
  { value: "Multifamily", label: "Ground-Up Construction" },
  { value: "Southern CA", label: "LA · Orange County" },
  { value: "Safety-First", label: "Project Delivery" },
];

export const VALUES = [
  {
    n: "01",
    title: "Precision",
    body: "Every detail is engineered before the first shovel breaks ground. We build to tolerances that protect schedule, budget, and design intent.",
  },
  {
    n: "02",
    title: "Integrity",
    body: "Transparent budgets, honest timelines, and a single point of accountability from preconstruction through final walkthrough.",
  },
  {
    n: "03",
    title: "Safety",
    body: "An industry-leading safety culture keeps our crews, partners, and communities protected on every active jobsite.",
  },
  {
    n: "04",
    title: "Long-Term Value",
    body: "We build assets that perform for decades — durable, efficient, and designed to elevate the neighborhoods they anchor.",
  },
];

export const SERVICES = [
  {
    n: "01",
    title: "Ground-Up Multifamily",
    body: "Full-scope construction of podium, wrap, and mid-rise communities — from foundations to final finishes across Southern California.",
    image: IMAGES.multifamily[0],
    points: ["Podium & wrap construction", "Mid-rise & mixed-use", "Affordable & market-rate"],
  },
  {
    n: "02",
    title: "Preconstruction",
    body: "Early budgeting, value engineering, and constructability review that de-risk your project long before mobilization.",
    image: IMAGES.team[2],
    points: ["Conceptual estimating", "Value engineering", "Constructability review"],
  },
  {
    n: "03",
    title: "General Contracting",
    body: "Disciplined field execution, trade coordination, and quality control delivered by seasoned superintendents.",
    image: IMAGES.construction[0],
    points: ["Trade coordination", "Schedule management", "Quality control"],
  },
  {
    n: "04",
    title: "Design-Build",
    body: "A single, accountable team uniting design and construction to compress schedules and protect design intent.",
    image: IMAGES.facade[0],
    points: ["Integrated delivery", "Single accountability", "Faster to occupancy"],
  },
  {
    n: "05",
    title: "Self-Perform Concrete",
    body: "In-house concrete and structural crews that give us direct control over the most critical path of every build.",
    image: IMAGES.construction[2],
    points: ["Foundations & podiums", "Post-tension decks", "Site concrete"],
  },
  {
    n: "06",
    title: "Sustainable Building",
    body: "Energy-efficient, code-forward construction that lowers operating cost and raises long-term asset performance.",
    image: IMAGES.facade[3],
    points: ["Title 24 compliance", "High-performance envelopes", "LEED-ready delivery"],
  },
];

// Currently active builds. Specs for the two Mariposa projects are authoritative
// (provided directly by the client) and must not be modified or estimated.
export const PROJECTS_UNDER_CONSTRUCTION = [
  {
    title: "Mariposa Avenue Residences I",
    location: "Mariposa Ave Area · Los Angeles County",
    status: "Under Construction",
    type: "Multifamily Residential",
    stories: "6 Stories",
    units: "20 Residential Units",
    size: "Approximately 22,000 SF",
    description:
      "A ground-up, six-story multifamily residential development delivering 20 modern residences to the Mariposa Avenue neighborhood. Built with precision, disciplined field coordination, and an unwavering commitment to safety and long-term value.",
    images: [
      "/media/projects/mariposa110-1.jpg",
      "/media/projects/mariposa110-2.jpg",
      "/media/projects/mariposa110-3.jpg",
      "/media/projects/mariposa110-4.jpg",
    ],
  },
  {
    title: "Mariposa Avenue Residences II",
    location: "Mariposa Ave Area · Los Angeles County",
    status: "Under Construction",
    type: "Multifamily Residential",
    stories: "6 Stories",
    units: "32 Residential Units",
    size: "Approximately 32,500 SF",
    description:
      "A ground-up, six-story multifamily residential development comprising 32 residences. This project reflects our end-to-end approach — careful preconstruction planning, experienced leadership, and superior craftsmanship from foundation through final turnover.",
    images: [
      "/media/projects/mariposa112-1.jpg",
      "/media/projects/mariposa112-2.jpg",
      "/media/projects/mariposa112-3.jpg",
      "/media/projects/mariposa112-4.jpg",
    ],
  },
];

// Delivered developments. Locations use broad area labels only (no street
// addresses); sizes/unit counts are approximate ranges for general reference.
// ARCHIVED: Completed projects are intentionally hidden from the public site.
// Data is preserved below in PROJECTS_COMPLETED_ARCHIVE and can be restored by
// pointing PROJECTS_COMPLETED back to this array.
const PROJECTS_COMPLETED_ARCHIVE = [
  {
    title: "Multifamily Residential Community",
    location: "Los Angeles County",
    status: "Completed",
    type: "Multifamily Residential",
    stories: "Mid-Rise",
    units: "Approx. 25–35 Residential Units",
    size: "Approx. 30,000–40,000 SF",
    description:
      "A completed ground-up multifamily residential community featuring contemporary architecture, high-quality finishes, and durable construction designed to elevate the surrounding neighborhood.",
    images: ["/media/projects/p1.jpg", "/media/projects/p2.jpg"],
  },
  {
    title: "Mid-Rise Multifamily Residences",
    location: "Los Angeles County",
    status: "Completed",
    type: "Multifamily Residential",
    stories: "Mid-Rise",
    units: "Approx. 20–30 Residential Units",
    size: "Approx. 25,000–35,000 SF",
    description:
      "A delivered multifamily residential development built with quality craftsmanship and efficient project management, adding refined new homes across Los Angeles County.",
    images: ["/media/projects/p3.jpg", "/media/projects/p4.jpg"],
  },
  {
    title: "Ground-Up Multifamily Development",
    location: "Los Angeles County",
    status: "Completed",
    type: "Multifamily Residential",
    stories: "Mid-Rise",
    units: "Approx. 15–25 Residential Units",
    size: "Approx. 18,000–25,000 SF",
    description:
      "A completed ground-up residential development that reflects our safety-first culture and schedule discipline, delivered with superior construction quality throughout.",
    images: ["/media/projects/p5.jpg"],
  },
];

// Public-facing completed projects list. Kept empty so only active/under-
// construction projects appear anywhere on the site. Restore by assigning
// PROJECTS_COMPLETED_ARCHIVE.
export const PROJECTS_COMPLETED = [];

export const POSITIONS = [
  "Project Manager",
  "Assistant Project Manager",
  "Superintendent",
  "Assistant Superintendent",
  "Project Engineer",
  "Estimator",
  "Preconstruction Manager",
  "Safety Manager",
  "Concrete Foreman",
  "Other / General Application",
];

export const PROJECT_TYPES = [
  "Ground-Up Multifamily",
  "Mixed-Use",
  "Affordable Housing",
  "Preconstruction Services",
  "Design-Build",
  "Other",
];

export const BENEFITS = [
  { title: "Competitive Compensation", body: "Market-leading salaries, performance bonuses, and profit sharing." },
  { title: "Full Health Coverage", body: "Medical, dental, and vision for you and your family from day one." },
  { title: "401(k) Matching", body: "Retirement planning with a generous company match and vesting." },
  { title: "Career Advancement", body: "Structured mentorship and clear paths from field to leadership." },
  { title: "Paid Time Off", body: "Generous PTO, paid holidays, and parental leave." },
  { title: "Continued Education", body: "Certifications, training, and tuition support to grow your craft." },
];

export const PARTNER_TRADES = [
  "Concrete & Foundations",
  "Structural Steel",
  "Framing & Rough Carpentry",
  "Mechanical / HVAC",
  "Electrical",
  "Plumbing",
  "Roofing & Waterproofing",
  "Drywall & Finishes",
  "Glazing & Storefront",
  "Landscape & Hardscape",
  "Fire Protection",
  "Elevators & Conveyance",
];

export const PARTNER_BENEFITS = [
  {
    n: "01",
    title: "Prompt, Reliable Payment",
    body: "Transparent billing and dependable pay cycles that respect the businesses we build with.",
  },
  {
    n: "02",
    title: "Consistent Pipeline",
    body: "A steady backlog of ground-up multifamily work across Southern California keeps your crews busy.",
  },
  {
    n: "03",
    title: "Safety-First Jobsites",
    body: "Well-run, organized sites with clear expectations that protect your team and your reputation.",
  },
  {
    n: "04",
    title: "True Partnership",
    body: "We treat subcontractors as partners — early involvement, fair terms, and long-term relationships.",
  },
];
